import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowsOut, Minus, Plus, FloppyDisk, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useGet } from "@/hooks/useResource";
import { Loading, PageHeader, ConfirmationStamp, StatusBadge } from "@/components/common";
import { ARBOURS, CEREMONY_LOCATIONS } from "@/lib/ceremonyOptions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EMPTY = {
  location: "",
  arbour_choice: "",
  guest_seating: "included_40",
  additional_chairs: 0,
  celebrant: "",
  celebrant_contact: "",
  stylist: "",
  stylist_contact: "",
  processional_song: "",
  recessional_song: "",
  readings: "",
  notes: "",
  start_time: "",
  client_status: "draft",
};

export default function Ceremony() {
  const { data, loading, refetch } = useGet("/client/ceremony");
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [zoomedArbour, setZoomedArbour] = useState(null);
  useEffect(() => {
    if (data) {
      setForm({
        ...EMPTY,
        ...data,
        additional_chairs: Number(data.additional_chairs || 0),
      });
    }
  }, [data]);
  const update = (key, value) => setForm({ ...form, [key]: value });
  const save = async (clientStatus = "draft") => {
    setSaving(true);
    try {
      await api.put("/client/ceremony", { ...form, client_status: clientStatus });
      setForm({ ...form, client_status: clientStatus });
      toast.success(clientStatus === "confirmed" ? "Ceremony confirmed." : "Ceremony draft saved.");
      refetch();
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setSaving(false);
    }
  };
  if (loading) {
    return <Loading label="Loading ceremony details" />;
  }
  return (
    <div>
      <PageHeader
        eyebrow="Your event"
        title="Ceremony"
        description={
          "Choose the setting and ceremony details your Gledswood team needs " +
          "to coordinate."
        }
      >
        <StatusBadge
          status={form.client_status}
          at={data?.confirmed_at}
          by={data?.confirmed_by}
          testid="ceremony-status-badge"
        />
        {form.client_status !== "confirmed" && (
          <>
            <Button
              onClick={() => save("draft")}
              disabled={saving}
              data-testid="ceremony-save-btn"
              className="rounded-full bg-primary hover:bg-primary/90"
            >
              <FloppyDisk size={18} className="mr-1" />
              {saving ? "Saving…" : "Save draft"}
            </Button>
            <Button
              onClick={() => save("confirmed")}
              disabled={saving}
              variant="outline"
              data-testid="ceremony-confirm-btn"
              className="rounded-full"
            >
              Confirm
            </Button>
          </>
        )}
      </PageHeader>
      {form.client_status === "confirmed" && (
        <div className="mb-5 flex flex-wrap items-center gap-3" data-testid="ceremony-confirmed-state">
          <ConfirmationStamp
            label="Confirmed"
            at={data?.confirmed_at}
            by={data?.confirmed_by}
            testid="ceremony-confirmed-stamp"
          />
          <p className="text-sm text-muted-foreground">
            Ask the Events Team if a correction is needed.
          </p>
        </div>
      )}
      <section data-testid="ceremony-location-options">
        <p className="eyebrow">Ceremony location</p>
        <div
          className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4"
          role="radiogroup"
          aria-label="Ceremony location"
        >
          {CEREMONY_LOCATIONS.map(([name, description]) => (
            <ChoiceCard
              key={name}
              name={name}
              description={description}
              selected={form.location === name}
              onSelect={() => form.client_status !== "confirmed" && update("location", name)}
              testId={`ceremony-location-${slug(name)}`}
            />
          ))}
        </div>
      </section>
      <section className="mt-10" data-testid="ceremony-arbour-options">
        <p className="eyebrow">Arbour choice</p>
        <h2 className="font-heading text-3xl text-primary mt-1">
          Choose your ceremony frame
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Flowers and fabric are not included with Gledswood arbour structures.
        </p>
        <div
          className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
          role="radiogroup"
          aria-label="Arbour choice"
        >
          {ARBOURS.map((option) => (
            <ArbourCard
              key={option.key}
              option={option}
              selected={form.arbour_choice === option.name}
              onSelect={() => form.client_status !== "confirmed" && update("arbour_choice", option.name)}
              onZoom={() => setZoomedArbour(option)}
            />
          ))}
        </div>
      </section>
      <div className="mt-10 grid grid-cols-1 gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <section
          className="border border-border bg-card p-6 rounded-lg"
          data-testid="ceremony-seating-panel"
        >
          <div className="flex items-start gap-3">
            <UsersThree size={24} className="text-secondary mt-1" />
            <div>
              <p className="eyebrow">Guest seating</p>
              <h2 className="font-heading text-2xl text-primary mt-1">
                Included seating
              </h2>
            </div>
          </div>
          <p className="mt-4 text-sm text-muted-foreground">
            Your ceremony package includes 40 guest chairs plus a signing table
            and chairs. This selection is automatically included.
          </p>
          <div className="mt-5 border-y border-border py-4">
            <p className="text-sm text-primary">
              {form.location === "Hennery Lawn"
                ? "40 mismatched chairs selected for Hennery Lawn."
                : "40 white folding chairs selected for your ceremony."}
            </p>
          </div>
          <div className="mt-5 flex items-center justify-between gap-4">
            <div>
              <p className="font-medium text-primary">Additional chairs</p>
              <p className="text-sm text-muted-foreground mt-1">
                Request estimate only: $4.40 per additional chair.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="rounded-full"
                onClick={() =>
                  update(
                    "additional_chairs",
                    Math.max(0, form.additional_chairs - 1),
                  )
                }
                aria-label="Remove additional chair"
                data-testid="additional-chairs-decrease"
              >
                <Minus size={16} />
              </Button>
              <output
                className="min-w-8 text-center font-heading text-xl text-primary"
                data-testid="additional-chairs-count"
              >
                {form.additional_chairs}
              </output>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="rounded-full"
                onClick={() => update("additional_chairs", Math.min(200, form.additional_chairs + 1))}
                aria-label="Add additional chair"
                data-testid="additional-chairs-increase"
              >
                <Plus size={16} />
              </Button>
            </div>
          </div>
        </section>
        <section className="border border-border bg-card p-6 rounded-lg">
          <p className="eyebrow">Ceremony contacts</p>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field
              label="Celebrant name"
              value={form.celebrant}
              onChange={(value) => update("celebrant", value)}
            />
            <Field
              label="Celebrant contact"
              value={form.celebrant_contact}
              onChange={(value) => update("celebrant_contact", value)}
            />
            <Field
              label="Stylist name"
              value={form.stylist}
              onChange={(value) => update("stylist", value)}
            />
            <Field
              label="Stylist contact"
              value={form.stylist_contact}
              onChange={(value) => update("stylist_contact", value)}
            />
          </div>
          <Link
            to="/suppliers"
            className="inline-block mt-5 text-sm text-secondary hover:text-primary"
            data-testid="ceremony-suppliers-link"
          >
            Manage all supplier contacts →
          </Link>
          <p className="mt-3 text-xs text-muted-foreground">
            Add a contact to My Suppliers only when you choose to do so; no preferred supplier is
            added automatically.
          </p>
        </section>
      </div>
      <section className="mt-6 border border-border bg-card p-6 rounded-lg">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label="Ceremony start time"
            type="time"
            value={form.start_time}
            onChange={(value) => update("start_time", value)}
          />
          <Field
            label="Processional song"
            value={form.processional_song}
            onChange={(value) => update("processional_song", value)}
          />
          <Field
            label="Recessional song"
            value={form.recessional_song}
            onChange={(value) => update("recessional_song", value)}
          />
          <TextField
            label="Readings"
            value={form.readings}
            onChange={(value) => update("readings", value)}
          />
          <TextField
            label="Notes"
            value={form.notes}
            onChange={(value) => update("notes", value)}
          />
        </div>
      </section>
      <Dialog open={!!zoomedArbour} onOpenChange={(open) => !open && setZoomedArbour(null)}>
        <DialogContent className="max-w-[min(94vw,820px)] p-4 sm:p-6" data-testid="arbour-zoom-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl sm:text-3xl text-primary pr-8">
              {zoomedArbour?.name}
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              {zoomedArbour?.description}
            </DialogDescription>
          </DialogHeader>
          {zoomedArbour && (
            <>
              <img
                src={zoomedArbour.image}
                alt={zoomedArbour.name}
                className="max-h-[66vh] w-full object-contain bg-muted rounded"
                data-testid="arbour-zoom-image"
              />
              <p className="mt-3 text-xs text-muted-foreground">
                Flowers and fabric are not included with Gledswood arbour structures.
              </p>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ChoiceCard({ name, description, selected, onSelect, testId }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      data-testid={testId}
      className={[
        "border p-5 rounded-lg text-left transition-colors duration-200",
        selected
          ? "border-secondary bg-secondary/10"
          : "border-border bg-card hover:bg-muted",
      ].join(" ")}
    >
      <p className="font-heading text-2xl text-primary">{name}</p>
      <p className="mt-2 text-sm text-muted-foreground">{description}</p>
    </button>
  );
}

function ArbourCard({ option, selected, onSelect, onZoom }) {
  return (
    <div className="relative">
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        onClick={onSelect}
        data-testid={`arbour-choice-${option.key}`}
        className={[
          "block w-full overflow-hidden border text-left rounded-lg transition-transform duration-200",
          "hover:-translate-y-0.5",
          selected
            ? "border-secondary ring-1 ring-secondary"
            : "border-border bg-card",
        ].join(" ")}
      >
        <div className="aspect-[4/5] bg-muted">
          <img
            src={option.image}
            alt={option.name}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="p-4">
          <p className="font-heading text-xl text-primary">{option.name}</p>
          <p className="mt-2 text-sm text-muted-foreground">
            {option.description}
          </p>
          <p
            className={`mt-3 text-xs ${selected ? "text-secondary" : "text-muted-foreground"}`}
          >
            {selected ? "Selected" : "Select option"}
          </p>
        </div>
      </button>
      <button
        type="button"
        onClick={onZoom}
        aria-label={`Enlarge ${option.name} image`}
        className="absolute right-2 top-2 flex items-center gap-1 bg-primary/85 px-2 py-1 text-[11px] uppercase tracking-wide text-primary-foreground rounded-full transition-colors duration-200 hover:bg-primary"
        data-testid={`arbour-zoom-${option.key}`}
      >
        <ArrowsOut size={11} />
        Enlarge
      </button>
    </div>
  );
}

function Field({ label, type = "text", value, onChange }) {
  const testId = `ceremony-${slug(label)}-input`;
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <Input
        type={type}
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2"
        data-testid={testId}
      />
    </div>
  );
}

function TextField({ label, value, onChange }) {
  return (
    <div className="sm:col-span-2">
      <Label className="eyebrow">{label}</Label>
      <Textarea
        value={value || ""}
        onChange={(event) => onChange(event.target.value)}
        className="mt-2"
        rows={3}
        data-testid={`ceremony-${slug(label)}-input`}
      />
    </div>
  );
}

function slug(value) {
  return value
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}
